import { loadBaseCSS } from '../shared/button-utils.js';

loadBaseCSS();

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * @element qds-inline-text-editor
 *
 * Lightweight inline text editor used on the content canvas for short semantic
 * text fields. It stages edits locally and emits `value-change` on commit.
 *
 * @attr {string} value - Initial text value
 * @attr {string} property - Config field name emitted with `value-change`
 * @attr {string} placeholder - Placeholder shown when empty
 * @attr {boolean} multiline - Allows line breaks when present
 * @attr {boolean} readonly - Disables editing when present
 *
 * @fires value-change - Fired when the editor commits a changed value
 */
export class QdsInlineTextEditor extends HTMLElement {
  private _property = '';
  private _value = '';
  private _draft = '';
  private _placeholder = '';
  private _multiline = false;
  private _readonly = false;
  private _focused = false;
  private _suppressCommit = false;

  static get observedAttributes(): string[] {
    return ['property', 'value', 'placeholder', 'multiline', 'readonly'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.render();
  }

  connectedCallback(): void {
    if (!this.shadowRoot) {
      return;
    }

    this.addEventListener('click', this._handleHostClick as EventListener);
    this.shadowRoot.addEventListener('focusin', this._handleFocusIn as EventListener);
    this.shadowRoot.addEventListener('focusout', this._handleFocusOut as EventListener);
    this.shadowRoot.addEventListener('input', this._handleInput as EventListener);
    this.shadowRoot.addEventListener('keydown', this._handleKeyDown as EventListener);
    this.shadowRoot.addEventListener('paste', this._handlePaste as EventListener);
    this._syncEditorText();
  }

  disconnectedCallback(): void {
    this.removeEventListener('click', this._handleHostClick as EventListener);
    this.shadowRoot?.removeEventListener('focusin', this._handleFocusIn as EventListener);
    this.shadowRoot?.removeEventListener('focusout', this._handleFocusOut as EventListener);
    this.shadowRoot?.removeEventListener('input', this._handleInput as EventListener);
    this.shadowRoot?.removeEventListener('keydown', this._handleKeyDown as EventListener);
    this.shadowRoot?.removeEventListener('paste', this._handlePaste as EventListener);
  }

  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null): void {
    switch (name) {
      case 'property':
        this._property = newValue || '';
        break;
      case 'value':
        this._value = newValue || '';
        if (!this._focused) {
          this._draft = this._value;
        }
        break;
      case 'placeholder':
        this._placeholder = newValue || '';
        break;
      case 'multiline':
        this._multiline = newValue !== null;
        break;
      case 'readonly':
        this._readonly = newValue !== null;
        break;
      default:
        break;
    }

    this.render();
  }

  set property(value: string | undefined) {
    this._property = value || '';
    if (value) {
      this.setAttribute('property', value);
    } else {
      this.removeAttribute('property');
    }
  }

  get property(): string {
    return this._property;
  }

  set value(value: unknown) {
    this._value = typeof value === 'string' ? value : '';
    if (!this._focused) {
      this._draft = this._value;
    }
    this.setAttribute('value', this._value);
    this.render();
  }

  get value(): string {
    return this._value;
  }

  set placeholder(value: string) {
    this._placeholder = value || '';
    if (value) {
      this.setAttribute('placeholder', value);
    } else {
      this.removeAttribute('placeholder');
    }
  }

  get placeholder(): string {
    return this._placeholder;
  }

  set multiline(value: boolean) {
    this._multiline = Boolean(value);
    if (this._multiline) {
      this.setAttribute('multiline', '');
    } else {
      this.removeAttribute('multiline');
    }
  }

  get multiline(): boolean {
    return this._multiline;
  }

  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    if (this._readonly) {
      this.setAttribute('readonly', '');
    } else {
      this.removeAttribute('readonly');
    }
  }

  get readonly(): boolean {
    return this._readonly;
  }

  private _getEditor(): HTMLDivElement | null {
    return this.shadowRoot?.querySelector<HTMLDivElement>('.inline-text-editor__surface') || null;
  }

  private _syncEditorText(): void {
    const editor = this._getEditor();
    if (!editor) {
      return;
    }

    const nextValue = this._focused ? this._draft : this._value;
    if (editor.textContent !== nextValue) {
      editor.textContent = nextValue;
    }
  }

  private _readEditorText(): string {
    const editor = this._getEditor();
    if (!editor) {
      return this._draft;
    }

    return this._multiline
      ? (editor.textContent || '')
      : (editor.textContent || '').replace(/\s+/g, ' ').trim();
  }

  private _commitDraft(): void {
    if (this._suppressCommit) {
      this._suppressCommit = false;
      return;
    }

    const nextValue = this._readEditorText();
    this._draft = nextValue;
    if (nextValue === this._value) {
      this.render();
      return;
    }

    this._value = nextValue;
    this.render();
    this.dispatchEvent(new CustomEvent('value-change', {
      detail: {
        property: this._property || undefined,
        value: nextValue
      },
      bubbles: true,
      composed: true
    }));
  }

  private _handleFocusIn = (): void => {
    if (this._readonly) {
      return;
    }

    this._focused = true;
    this._draft = this._value;
    this._syncEditorText();
  };

  private _handleFocusOut = (event: FocusEvent): void => {
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && this.shadowRoot?.contains(nextTarget)) {
      return;
    }

    this._focused = false;
    this._commitDraft();
  };

  private _handleInput = (): void => {
    this._draft = this._readEditorText();
  };

  private _handleHostClick = (event: MouseEvent): void => {
    if (this._readonly) {
      return;
    }

    const path = event.composedPath();
    const clickedSurface = path.some((entry) =>
      entry instanceof HTMLElement && entry.classList.contains('inline-text-editor__surface')
    );

    if (!clickedSurface) {
      this._getEditor()?.focus();
    }
  };

  private _handleKeyDown = (event: KeyboardEvent): void => {
    if (this._readonly) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this._suppressCommit = true;
      this._focused = false;
      this._draft = this._value;
      this.render();
      this._getEditor()?.blur();
      return;
    }

    if (!this._multiline && event.key === 'Enter') {
      event.preventDefault();
      this._focused = false;
      this._commitDraft();
      this._getEditor()?.blur();
    }
  };

  private _handlePaste = (event: ClipboardEvent): void => {
    if (this._readonly) {
      return;
    }

    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') || '';
    const normalized = this._multiline ? text : text.replace(/\s+/g, ' ');

    if (typeof document.execCommand === 'function') {
      document.execCommand('insertText', false, normalized);
      return;
    }

    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return;
    }

    selection.deleteFromDocument();
    selection.getRangeAt(0).insertNode(document.createTextNode(normalized));
    selection.collapseToEnd();
  };

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    const editorValue = this._focused ? this._draft : this._value;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          min-width: 0;
          color: inherit;
          font: inherit;
        }

        .inline-text-editor {
          display: block;
          min-width: 0;
        }

        .inline-text-editor__surface {
          display: block;
          width: 100%;
          min-width: 0;
          min-height: 1.2em;
          padding: 2px 4px;
          border-radius: 4px;
          border: 1px dashed transparent;
          background: transparent;
          color: inherit;
          font: inherit;
          line-height: inherit;
          white-space: ${this._multiline ? 'pre-wrap' : 'nowrap'};
          overflow-wrap: anywhere;
          outline: none;
          cursor: ${this._readonly ? 'default' : 'text'};
          transition: border-color 0.15s ease, background-color 0.15s ease;
        }

        .inline-text-editor__surface:hover {
          border-color: ${this._readonly ? 'transparent' : 'var(--theme-border-primary, #dfe1e5)'};
          background: ${this._readonly ? 'transparent' : 'var(--theme-background-secondary, rgba(248, 248, 248, 0.65))'};
        }

        .inline-text-editor__surface:focus {
          border-color: var(--theme-accent-primary, #4285f4);
          background: var(--theme-background-primary, #ffffff);
          box-shadow: 0 0 0 2px var(--button-focus-ring, rgba(66, 133, 244, 0.2));
        }

        .inline-text-editor__surface:empty::before {
          content: "${escapeHtml(this._placeholder)}";
          color: var(--theme-text-tertiary, #80868b);
          pointer-events: none;
        }
      </style>
      <div class="inline-text-editor">
        <div
          class="inline-text-editor__surface"
          contenteditable="${this._readonly ? 'false' : 'true'}"
          spellcheck="true"
          role="textbox"
          aria-multiline="${this._multiline ? 'true' : 'false'}"
          data-frame-ignore-select="true"
        >${escapeHtml(editorValue)}</div>
      </div>
    `;

    this._syncEditorText();
  }
}

if (!customElements.get('qds-inline-text-editor')) {
  customElements.define('qds-inline-text-editor', QdsInlineTextEditor);
}

export default QdsInlineTextEditor;
